import React, { FC } from "react";
import { useNavigate } from "react-router-dom";

import { ActivityLogEntry } from "./activityLog.types";
import {
  activityInitials,
  activityLink,
  activitySentence,
  activityTimeLabel,
  AUTOMATIC_NAME,
  groupActivityByDay,
} from "./activityLog.utils";

const DEFAULT_COLOR = "rgb(30, 58, 138)";

const PersonIcon = () => (
  <svg
    width={"11"}
    height={"11"}
    viewBox={"0 0 24 24"}
    fill={"none"}
    stroke={"currentColor"}
    strokeWidth={"2"}
    strokeLinecap={"round"}
    strokeLinejoin={"round"}>
    <path d={"M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"} />
    <circle cx={"12"} cy={"7"} r={"4"} />
  </svg>
);

const BuildingIcon = () => (
  <svg
    width={"11"}
    height={"11"}
    viewBox={"0 0 24 24"}
    fill={"none"}
    stroke={"currentColor"}
    strokeWidth={"2"}
    strokeLinecap={"round"}
    strokeLinejoin={"round"}>
    <path d={"M3 21h18"} />
    <path d={"M5 21V7l7-4 7 4v14"} />
    <path d={"M9 9h0M9 13h0M9 17h0M15 9h0M15 13h0M15 17h0"} />
  </svg>
);

type Props = {
  entries: ActivityLogEntry[];
  /** Work item Log tab: no client line, no links, work name left out. */
  inWork?: boolean;
};

export const ActivityLogList: FC<Props> = ({ entries, inWork = false }) => {
  const navigate = useNavigate();
  const groups = groupActivityByDay(entries);

  return (
    <>
      {groups.map((group) => (
        <div key={group.day} className={inWork ? "mb-[22px]" : "mb-[26px]"}>
          <div
            className={`${
              inWork ? "mb-[11px]" : "mb-3"
            } text-[12px] font-extrabold uppercase tracking-[0.5px] text-[#9097A8]`}>
            {group.day}
          </div>
          <div
            className={
              "overflow-hidden rounded-[13px] border border-[#E8E8EC] bg-white"
            }>
            {group.entries.map((entry, index) => {
              const link = inWork ? null : activityLink(entry);
              const isLast = index === group.entries.length - 1;
              const automatic = entry.userName === AUTOMATIC_NAME;
              return (
                <div
                  key={entry.id}
                  data-testid={"activity-row"}
                  onClick={link ? () => navigate(link) : undefined}
                  className={`flex items-center gap-[14px] px-[18px] py-[14px] ${
                    isLast ? "" : "border-b border-[#F1F2F5]"
                  } ${
                    link
                      ? "cursor-pointer transition-colors hover:bg-[#F7F8FA]"
                      : ""
                  }`}>
                  <span
                    className={
                      "flex h-6 w-6 flex-none items-center justify-center rounded-full text-[10px] font-bold text-white"
                    }
                    style={{
                      background: automatic
                        ? "#9AA0B0"
                        : entry.userColor || DEFAULT_COLOR,
                    }}>
                    {activityInitials(entry.userName)}
                  </span>
                  <span className={"min-w-0 flex-1"}>
                    <span className={"block text-[14px] leading-[1.5]"}>
                      <span className={"font-bold text-[#1F2433]"}>
                        {entry.userName}
                      </span>{" "}
                      {activitySentence(entry, inWork).map((segment, i) => (
                        <span
                          key={i}
                          className={
                            segment.bold
                              ? "font-bold text-[#1F2433]"
                              : "font-medium text-[#5B6678]"
                          }>
                          {segment.text}
                        </span>
                      ))}
                    </span>
                    {!inWork && entry.customerName && (
                      <span
                        className={
                          "mt-[3px] inline-flex items-center gap-[5px] text-[12px] text-[#9097A8]"
                        }>
                        {entry.isOrganization ? (
                          <BuildingIcon />
                        ) : (
                          <PersonIcon />
                        )}
                        {entry.customerName}
                      </span>
                    )}
                  </span>
                  <span
                    className={
                      "flex-none whitespace-nowrap text-[12px] font-medium text-[#9097A8]"
                    }>
                    {activityTimeLabel(entry.createdAt)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
};
